import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Printer, Calendar, Download } from "lucide-react";
import AxiosDB from "@/Config/AxiosDB";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { handleDescargarExcel } from "./Reportes_handleDescargarExcel";

const MESES = [
    { value: "1", label: "Enero" },
    { value: "2", label: "Febrero" },
    { value: "3", label: "Marzo" },
    { value: "4", label: "Abril" },
    { value: "5", label: "Mayo" },
    { value: "6", label: "Junio" },
    { value: "7", label: "Julio" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Septiembre" },
    { value: "10", label: "Octubre" },
    { value: "11", label: "Noviembre" },
    { value: "12", label: "Diciembre" },
];

export default function Reportes({ user, onLogout }) {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const [ano, setAno] = useState(currentYear.toString());
    const [mes, setMes] = useState(currentMonth.toString());
    const [reporte, setReporte] = useState(null);
    const [loading, setLoading] = useState(false);

    const anos = Array.from({ length: 5 }, (_, i) => currentYear - i);

    const loadReporte = async () => {
        setLoading(true);
        try {
            const response = await AxiosDB.get(`/reportes/facturacion`, {
                params: { ano: parseInt(ano), mes: parseInt(mes) }
            });
            setReporte(response.data);
        } catch (error) {
            toast.error("Error al cargar el reporte");
        } finally {
            setLoading(false);
        }
    };

    const ocultarNoImprimir = () => {
        const elemento = document.getElementById("notprintDocument");

        if (elemento) {
            elemento.style.display = "none";
        }
    };
    const MostrarNoImprimir = () => {
        const elemento = document.getElementById("notprintDocument");

        if (elemento) {
            elemento.style.display = "block";
        }
    };

    const handleImprimir = () => {
        ocultarNoImprimir()
        const contenido = document.getElementById("printDocument").innerHTML;

        const estilos = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
            .map((el) => el.outerHTML)
            .join("\n");

        const ventana = window.open("", "_blank", "width=900,height=700");

        ventana.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Imprimir</title>
            ${estilos}

            <style>
                /* Forzar tamaño de letra en toda la impresión */
                html, body, body * {
                    font-size: 10px !important;
                }

                body {
                    font-family: Arial, Helvetica, sans-serif;
                    margin: 0;
                    padding: 15px;
                    background: white;
                }

                * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                }

                td, th {
                    padding: 3px !important;
                }

                @media print {
                    body {
                        margin: 0;
                    }
                }
            </style>

        </head>

        <body>
            ${contenido}
        </body>

        </html>
    `);

        ventana.document.close();

        ventana.onload = () => {
            ventana.focus();
            ventana.print();
            ventana.close();
        };

        MostrarNoImprimir()
    };
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-DO', {
            style: 'currency',
            currency: 'DOP'
        }).format(amount);
    };

    const getMesNombre = (mesNum) => {
        return MESES.find(m => m.value === mesNum.toString())?.label || '';
    };

    const totalventa = (reporte && reporte?.resumen || [])
        .filter(r => ['E31', 'E32', 'E44', 'E45'].includes(r.tipo))
        .reduce((acc, item) => {
            acc.cantidad += Number(item.cantidad || 0);
            acc.impuesto += Number(item.impuesto || 0);
            acc.monto += Number(item.monto || 0);
            acc.efectivo += Number(item.efectivo || 0);
            acc.cheque_transferencia_deposito += Number(item.cheque_transferencia_deposito || 0);
            acc.tarjeta_debito_credito += Number(item.tarjeta_debito_credito || 0);
            acc.venta_a_credito += Number(item.venta_a_credito || 0);
            acc.permuta += Number(item.permuta || 0);
            acc.nota_de_credito += Number(item.nota_de_credito || 0);
            acc.otras_formas_pago += Number(item.otras_formas_pago || 0);
            return acc;
        }, {
            cantidad: 0,
            impuesto: 0,
            monto: 0,
            efectivo: 0,
            cheque_transferencia_deposito: 0,
            tarjeta_debito_credito: 0,
            venta_a_credito: 0,
            permuta: 0,
            nota_de_credito: 0,
            otras_formas_pago: 0
        });


    const totalcredito = (reporte && reporte?.resumen || [])
        .filter(r => ['E34'].includes(r.tipo))
        .reduce((acc, item) => {
            acc.cantidad += Number(item.cantidad || 0);
            acc.impuesto += Number(item.impuesto || 0);
            acc.monto += Number(item.monto || 0);
            acc.efectivo += Number(item.efectivo || 0);
            acc.cheque_transferencia_deposito += Number(item.cheque_transferencia_deposito || 0);
            acc.tarjeta_debito_credito += Number(item.tarjeta_debito_credito || 0);
            acc.venta_a_credito += Number(item.venta_a_credito || 0);
            acc.permuta += Number(item.permuta || 0);
            acc.nota_de_credito += Number(item.nota_de_credito || 0);
            acc.otras_formas_pago += Number(item.otras_formas_pago || 0);
            return acc;
        }, {
            cantidad: 0,
            impuesto: 0,
            monto: 0,
            efectivo: 0,
            cheque_transferencia_deposito: 0,
            tarjeta_debito_credito: 0,
            venta_a_credito: 0,
            permuta: 0,
            nota_de_credito: 0,
            otras_formas_pago: 0
        });

    return (
        <Layout user={user} onLogout={onLogout} title="Reportes de Facturación">
            {/* Controles de filtro - se ocultan al imprimir */}
            <Card className="mb-6 print:hidden" data-testid="reportes-filters">
                <CardHeader>
                    <CardTitle>Generar Reporte</CardTitle>
                    <CardDescription>
                        Selecciona el período de facturación para generar el reporte
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex items-end gap-4">
                        <div className="w-40">
                            <Label htmlFor="ano" className="text-sm">Año</Label>
                            <Select value={ano} onValueChange={setAno}>
                                <SelectTrigger data-testid="ano-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {anos.map(a => (
                                        <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="w-48">
                            <Label htmlFor="mes" className="text-sm">Mes</Label>
                            <Select value={mes} onValueChange={setMes}>
                                <SelectTrigger data-testid="mes-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {MESES.map(m => (
                                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={loadReporte} disabled={loading} data-testid="generar-reporte-button">
                            <FileText className="w-4 h-4 mr-2" />
                            {loading ? "Generando..." : "Generar Reporte"}
                        </Button>
                        {reporte && (
                            <>
                                <Button onClick={() => handleDescargarExcel(reporte, user)} variant="outline" className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200" data-testid="descargar-excel-button">
                                    <Download className="w-4 h-4 mr-2" />
                                    Descargar Excel
                                </Button>
                                <Button onClick={handleImprimir} variant="outline" data-testid="imprimir-button">
                                    <Printer className="w-4 h-4 mr-2" />
                                    Imprimir
                                </Button>
                            </>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Reporte - diseñado para impresión */}
            {reporte && (

                <div className="reporte-container bg-white rounded-lg shadow-sm p-8" data-testid="reporte-content" id="printDocument">
                    {/* Encabezado del Reporte */}
                    <div className="mb-8 border-b-2 border-blue-600 pb-4">
                        <div className="flex items-start justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                                    Reporte de Facturación
                                </h1>
                                <div className="flex items-center gap-2 text-slate-600">
                                    <Calendar className="w-4 h-4" />
                                    <span className="font-semibold">
                                        Período: {getMesNombre(reporte.periodo.mes)} {reporte.periodo.ano}
                                    </span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm text-slate-600">Empresa</div>
                                <div className="font-bold text-slate-800">{user?.nombre}</div>
                                <div className="text-sm text-slate-600">RNC: {user?.rnc}</div>
                            </div>
                        </div>
                    </div>

                    {/* Resumen de Facturación  */}
                    <section className="mb-8">
                        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            <div className="w-1 h-6 bg-blue-600 rounded"></div>
                            Resumen de Facturación -Ventas Emitidas
                        </h2>
                        <div className="overflow-hidden rounded-lg border border-slate-200">
                            <table className="w-full">
                                <thead className="bg-blue-600 text-white">
                                    <tr>
                                        <th className="text-left py-3 px-4 font-semibold">Tipo de Comprobante</th>
                                        <th className="text-left py-3 px-4 font-semibold">Descripcion</th>
                                        <th className="text-center py-3 px-4 font-semibold">Cantidad Aprobado</th>
                                        <th className="text-right py-3 px-4 font-semibold">Monto</th>
                                        <th className="text-right py-3 px-4 font-semibold">Impuesto (ITBIS)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reporte.resumen.filter(r => ['E31', 'E32', 'E44', 'E45'].includes(r.tipo)).map((item, index) => (
                                        <tr key={item.tipo} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                            <td className="py-3 px-4 font-semibold text-slate-700">{item.tipo}</td>
                                            <td className="py-3 px-4 font-semibold text-slate-700">{item.descripcion}</td>
                                            <td className="py-3 px-4 text-center font-semibold text-slate-700">{item.cantidad}</td>
                                            <td className="py-3 px-4 text-right">{formatCurrency(item.monto)}</td>
                                            <td className="py-3 px-4 text-right">{formatCurrency(item.impuesto)}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-blue-100 border-t-2 border-blue-600">
                                        <td className="py-3 px-4 font-bold text-slate-800">TOTALES</td>
                                        <td className="py-3 px-4 font-bold text-slate-800"></td>
                                        <td className="py-3 px-4 text-center font-bold text-slate-800">{totalventa.cantidad}</td>
                                        <td className="py-3 px-4 text-right font-bold text-slate-800">{formatCurrency(totalventa.monto)}</td>
                                        <td className="py-3 px-4 text-right font-bold text-slate-800">{formatCurrency(totalventa.impuesto)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                    <section className="mb-8">
                        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            <div className="w-1 h-6 bg-blue-600 rounded"></div>
                            Resumen de Facturación -Ventas Netas- (ventas - Devoluciones)
                        </h2>
                        <div className="overflow-hidden rounded-lg border border-slate-200">
                            <table className="w-full">
                                <thead className="bg-blue-600 text-white">
                                    <tr>
                                        <th className="text-left py-3 px-4 font-semibold">Tipo de Comprobante</th>
                                        <th className="text-left py-3 px-4 font-semibold">Descripcion</th>
                                        <th className="text-center py-3 px-4 font-semibold">Cantidad Aprobado</th>
                                        <th className="text-right py-3 px-4 font-semibold">Monto</th>
                                        <th className="text-right py-3 px-4 font-semibold">Impuesto (ITBIS)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reporte?.resumen.filter(r => ['E34'].includes(r.tipo)).map((item, index) => (
                                        <tr key={item.tipo} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                            <td className="py-3 px-4 font-semibold text-slate-700">{item.tipo}</td>
                                            <td className="py-3 px-4 font-semibold text-slate-700">{item.descripcion}</td>
                                            <td className="py-3 px-4 text-center font-semibold text-slate-700">{item.cantidad}</td>
                                            <td className="py-3 px-4 text-right">{formatCurrency(item.monto)}</td>
                                            <td className="py-3 px-4 text-right">{formatCurrency(item.impuesto)}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-blue-100 border-t-2 border-blue-600">
                                        <td className="py-3 px-4 font-bold text-slate-800">TOTALES</td>
                                        <td className="py-3 px-4 font-bold text-slate-800"></td>
                                        <td className="py-3 px-4 text-center font-bold text-slate-800"></td>
                                        <td className="py-3 px-4 text-right font-bold text-slate-800">{formatCurrency(totalventa.monto - totalcredito.monto)}</td>
                                        <td className="py-3 px-4 text-right font-bold text-slate-800">{formatCurrency(totalventa.impuesto - totalcredito.impuesto)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                    <section className="mb-8">
                        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            <div className="w-1 h-6 bg-blue-600 rounded"></div>
                            Otros Documentos Emitidos
                        </h2>
                        <div className="overflow-hidden rounded-lg border border-slate-200">
                            <table className="w-full">
                                <thead className="bg-blue-600 text-white">
                                    <tr>
                                        <th className="text-left py-3 px-4 font-semibold">Tipo de Comprobante</th>
                                        <th className="text-left py-3 px-4 font-semibold">Descripcion</th>
                                        <th className="text-center py-3 px-4 font-semibold">Cantidad Aprobado</th>
                                        <th className="text-right py-3 px-4 font-semibold">Monto</th>
                                        <th className="text-right py-3 px-4 font-semibold">Impuesto (ITBIS)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reporte?.resumen.filter(r => !['E31', 'E32', 'E44', 'E45', 'E34'].includes(r.tipo)).map((item, index) => (
                                        <tr key={item.tipo} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                            <td className="py-3 px-4 font-semibold text-slate-700">{item.tipo}</td>
                                            <td className="py-3 px-4 font-semibold text-slate-700">{item.descripcion}</td>
                                            <td className="py-3 px-4 text-center font-semibold text-slate-700">{item.cantidad}</td>
                                            <td className="py-3 px-4 text-right">{formatCurrency(item.monto)}</td>
                                            <td className="py-3 px-4 text-right">{formatCurrency(item.impuesto)}</td>
                                        </tr>
                                    ))}

                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Lista de Facturas Aprobadas */}
                    <section className="mb-8 page-break-before" id="notprintDocument">
                        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            <div className="w-1 h-6 bg-green-600 rounded"></div>
                            Lista de Facturas Emitidas y Aprobadas
                        </h2>
                        <Tabs defaultValue="payload" className="w-full">
                            <TabsList className="grid w-full grid-cols-10">
                                {
                                    reporte.resumen.map(r => {
                                        return (<TabsTrigger value={r.tipo} data-testid="payload-tab">{r.tipo} ( {reporte.facturas_aprobadas.filter(f => f.tipo === r.tipo).length} ) </TabsTrigger>)
                                    })
                                }
                                <TabsTrigger value='rechazada' data-testid="payload-tab">Rechazadas</TabsTrigger>
                                <TabsTrigger value='anuladas' data-testid="payload-tab">Anuladas</TabsTrigger>
                            </TabsList>
                            {
                                reporte.resumen.map((r) => {
                                    return (
                                        <TabsContent value={r.tipo} className="mt-4  ">

                                            {reporte.facturas_aprobadas.filter(f => f.tipo === r.tipo).length === 0 ? (
                                                <p className="text-slate-600 py-4">No hay facturas aprobadas en este período</p>
                                            ) : (
                                                <div className="overflow-hidden rounded-lg border border-slate-200">
                                                    <table className="w-full text-sm">
                                                        <thead className="bg-green-600 text-white">
                                                            <tr>
                                                                <th className="text-left py-2 px-3 font-semibold">Fecha</th>
                                                                <th className="text-left py-2 px-3 font-semibold">NCF</th>
                                                                <th className="text-left py-2 px-3 font-semibold">RNC</th>
                                                                <th className="text-left py-2 px-3 font-semibold">Cliente</th>
                                                                <th className="text-right py-2 px-3 font-semibold">Monto</th>
                                                                <th className="text-right py-2 px-3 font-semibold">ITBIS</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {reporte.facturas_aprobadas.filter(f => f.tipo === r.tipo).map((factura, index) => (
                                                                <tr key={factura.id} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                                                    <td className="py-2 px-3">{factura.fecha_emision}</td>
                                                                    <td className="py-2 px-3 font-mono text-xs">{factura.ncf}</td>
                                                                    <td className="py-2 px-3">{factura.rnc}</td>
                                                                    <td className="py-2 px-3">{factura.nombre}</td>
                                                                    <td className="py-2 px-3 text-right">{formatCurrency(factura.monto)}</td>
                                                                    <td className="py-2 px-3 text-right">{formatCurrency(factura.itbis)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                            <div className="mt-2 text-sm text-slate-600">
                                                Total de facturas aprobadas: <span className="font-bold">{reporte.facturas_aprobadas.filter(f => f.tipo === r.tipo).length}</span>
                                            </div>

                                        </TabsContent>
                                    )
                                })
                            }

                            <TabsContent value='rechazada' className="mt-4" >

                                {/* Lista de Facturas Rechazadas */}
                                {reporte.facturas_rechazadas.length > 0 && (
                                    <section className="mb-8 page-break-before">
                                        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                                            <div className="w-1 h-6 bg-red-600 rounded"></div>
                                            Lista de Facturas Rechazadas
                                        </h2>
                                        <div className="overflow-hidden rounded-lg border border-slate-200">
                                            <table className="w-full text-sm">
                                                <thead className="bg-red-600 text-white">
                                                    <tr>
                                                        <th className="text-left py-2 px-3 font-semibold">Fecha</th>
                                                        <th className="text-left py-2 px-3 font-semibold">NCF</th>
                                                        <th className="text-left py-2 px-3 font-semibold">RNC</th>
                                                        <th className="text-left py-2 px-3 font-semibold">Cliente</th>
                                                        <th className="text-right py-2 px-3 font-semibold">Monto</th>
                                                        <th className="text-left py-2 px-3 font-semibold">Estado</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reporte.facturas_rechazadas.map((factura, index) => (
                                                        <tr key={factura.id} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                                            <td className="py-2 px-3">{factura.fecha_emision}</td>
                                                            <td className="py-2 px-3 font-mono text-xs">{factura.ncf}</td>
                                                            <td className="py-2 px-3">{factura.rnc}</td>
                                                            <td className="py-2 px-3">{factura.nombre}</td>
                                                            <td className="py-2 px-3 text-right">{formatCurrency(factura.monto)}</td>
                                                            <td className="py-2 px-3">
                                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                                                    {factura.estado_firma}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="mt-2 text-sm text-slate-600">
                                            Total de facturas rechazadas: <span className="font-bold">{reporte.facturas_rechazadas.length}</span>
                                        </div>
                                    </section>
                                )}

                            </TabsContent>
                            <TabsContent value='anuladas' className="mt-4  ">

                                {/* Lista de Facturas Rechazadas */}
                                {reporte.facturas_anuladas.length > 0 && (
                                    <section className="mb-8 page-break-before">
                                        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                                            <div className="w-1 h-6 bg-red-600 rounded"></div>
                                            Lista de Facturas Anuladas
                                        </h2>
                                        <div className="overflow-hidden rounded-lg border border-slate-200">
                                            <table className="w-full text-sm">
                                                <thead className="bg-red-600 text-white">
                                                    <tr>
                                                        <th className="text-left py-2 px-3 font-semibold">Fecha</th>
                                                        <th className="text-left py-2 px-3 font-semibold">NCF</th>
                                                        <th className="text-left py-2 px-3 font-semibold">RNC</th>
                                                        <th className="text-left py-2 px-3 font-semibold">Cliente</th>
                                                        <th className="text-right py-2 px-3 font-semibold">Monto</th>
                                                        <th className="text-left py-2 px-3 font-semibold">Estado</th>
                                                        <th className="text-left py-2 px-3 font-semibold">Fecha de Anulacion</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reporte.facturas_anuladas.map((factura, index) => (
                                                        <tr key={factura.id} className={index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                                                            <td className="py-2 px-3">{factura.fecha_emision}</td>
                                                            <td className="py-2 px-3 font-mono text-xs">{factura.ncf}</td>
                                                            <td className="py-2 px-3">{factura.rnc}</td>
                                                            <td className="py-2 px-3">{factura.nombre}</td>
                                                            <td className="py-2 px-3 text-right">{formatCurrency(factura.monto)}</td>
                                                            <td className="py-2 px-3">
                                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                                                    {factura.estado_firma}
                                                                </span>
                                                            </td>
                                                            <td className="py-2 px-3">
                                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                                                    {factura.response_anulacion[0].stampDate}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="mt-2 text-sm text-slate-600">
                                            Total de facturas Anuladas: <span className="font-bold">{reporte.facturas_anuladas.length}</span>
                                        </div>
                                    </section>
                                )}

                            </TabsContent>
                        </Tabs>

                    </section>



                    {/* Pie de página */}
                    <div className="mt-12 pt-6 border-t border-slate-200 text-center text-sm text-slate-600">
                        <p>Reporte generado el {new Date().toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        <p className="mt-1">Sistema de Gestión Fiscal - Buzon Fiscal</p>
                    </div>
                </div>
            )}

            {!reporte && !loading && (
                <div className="text-center py-12 text-slate-600">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                    <p className="text-lg">Selecciona un período y genera el reporte</p>
                </div>
            )}
        </Layout>
    );
}